// The two screens before the cards: the entry form (who you are, which server, create or join)
// and the room itself (code to read out, who is seated, start).
//
// Every option in the form comes from the server catalogue, never from a list kept here: the games,
// their table sizes and their decks are decided by the engine, and a client that invented its own
// copy would sooner or later offer a room the server refuses.

import { el, clear } from './dom.js';
import { accountToken, sessionToken } from '../net/identity.js';
import { Protocol, ServerProtocol } from '../generated/protocol.js';

const StorageKeys = {
  playerName: 'marz.playerName',
  serverUrl: 'marz.serverUrl',
};

// The port the game server listens on when nothing proxies it, and the address it has on the
// machine that runs it: the last resort of the search below
const GameServerPort = 8090;
const LocalServerUrl = `ws://localhost:${GameServerPort}`;

// The one game anybody can start without an account: the one the whole thing is named after.
// Everything else is listed with a padlock and opens on signing in — the same rule as the Android
// app, and for the same reason: a guest can play and be invited, but the games are what an account
// is worth. Entering a room with a code is never locked, so an invited friend always sits down.
const MainGame = 'MARZGAME';

// Which tables this client has. The catalogue says what the ENGINE can deal; this says what the
// web client can draw, which is a different question and rightly lives here. Scopa is not played
// in tricks at all and needs a table of its own, exactly as it has its own fragment on Android
export const UNSUPPORTED_GAMES = new Set(['SCOPA', 'SCOPONE', 'SCOPONE_SCIENTIFICO']);

function remembered(key, fallback) {
  try {
    return localStorage.getItem(key) ?? fallback;
  } catch {
    // Private browsing or blocked storage: the form simply starts empty
    return fallback;
  }
}

function remember(key, value) {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Not being able to remember the name is not worth interrupting anyone over
  }
}

// The addresses of the server, worked out instead of typed. The page already says most of it:
// served from the deployed site, the server is the same origin behind the /ws proxy; served by the
// dev server or off a PC on the Wi-Fi, it is the JVM on its own port on the same machine. Tried in
// order, first that answers wins, so in the normal case nobody ever opens the server field.
//
// The remembered one goes first, because it is the only one somebody chose on purpose — but it is
// not the only one tried, or a stale address left over from an evening in LAN would keep a browser
// from ever reaching the site it is now opened from.
function candidateServerUrls() {
  const candidates = [];
  const saved = remembered(StorageKeys.serverUrl, '');
  if (saved) candidates.push(saved);
  const { protocol, host, hostname } = globalThis.location ?? {};
  if (protocol === 'https:') {
    candidates.push(`wss://${host}/ws`);
    // A page in https can only open a wss: anything else the browser refuses as mixed content,
    // so trying it would only cost an error in the console
    return [...new Set(candidates)].filter((url) => url.startsWith('wss://'));
  }
  if (protocol === 'http:' && host) {
    candidates.push(`ws://${host}/ws`);
    candidates.push(`ws://${hostname}:${GameServerPort}`);
  }
  candidates.push(LocalServerUrl);
  return [...new Set(candidates)];
}

// What to call a server on screen: the host, without the scheme and the /ws nobody needs to read
function serverLabel(url) {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
}

export class EntryForm {
  #root;
  #nameInput;
  #identity;
  #lockedHint;
  #serverInput;
  #serverDetails;
  #gameSelect;
  #playersSelect;
  #difficultySelect;
  #codeInput;
  #status;
  #catalogue = null;
  // The signed-in account, or null for a guest. It decides the name at the table, so it also
  // decides what the top of this form looks like
  #account = null;
  #handlers;

  // handlers: { onCreate, onJoin, onLoadCatalogue, onStats, onRules, onAccount }
  constructor(handlers) {
    this.#handlers = handlers;
    this.#build();
  }

  get element() {
    return this.#root;
  }

  get serverUrl() {
    return this.#serverInput.value.trim();
  }

  // Whoever is signed in, from app.js. Null puts the name field back, which is how a guest plays
  setAccount(account) {
    this.#account = account;
    this.#renderIdentity();
    // The padlocks belong to whoever is signed in, so they are redrawn here and not only on load
    if (this.#catalogue) this.#fillFromCatalogue();
  }

  // The name of the game selected in the form, as the server calls it. Empty until the catalogue
  // has arrived, which is fine: the rules screen falls back to its first game
  get selectedGameName() {
    return this.#selectedGame()?.[ServerProtocol.DisplayNameID] ?? '';
  }

  #build() {
    this.#nameInput = el('input', {
      id: 'playerName',
      type: 'text',
      maxlength: '20',
      placeholder: 'Il tuo nome',
      value: remembered(StorageKeys.playerName, ''),
    });
    this.#serverInput = el('input', {
      id: 'serverUrl',
      type: 'text',
      placeholder: 'ws://...',
      'aria-label': 'Indirizzo del server',
      value: candidateServerUrls()[0],
      // Typed by hand: from here on that address is the one to use, and the search is off
      onchange: () => this.loadCatalogue(this.serverUrl),
    });
    // Out of the way but one click away: the address is found on its own, and this is only for the
    // setups the search cannot guess. It opens by itself when nothing answered
    this.#serverDetails = el('details', { className: 'advanced' }, [
      el('summary', { text: 'Indirizzo del server' }),
      this.#serverInput,
    ]);

    this.#gameSelect = el('select', {
      id: 'gameType',
      onchange: () => this.#gameChosen(),
    });
    // Shown to a guest only: the padlocks would otherwise be a wall with nothing written on it
    this.#lockedHint = el('button', {
      className: 'link-button',
      type: 'button',
      hidden: true,
      text: 'Con un account gratuito sblocchi tutti gli altri giochi.',
      onclick: () => this.#handlers.onAccount(),
    });
    this.#playersSelect = el('select', { id: 'playersNumber' });
    this.#difficultySelect = el('select', { id: 'difficulty' });
    this.#codeInput = el('input', {
      id: 'roomCode',
      type: 'text',
      maxlength: '4',
      placeholder: 'ABCD',
      autocapitalize: 'characters',
      oninput: (event) => {
        event.target.value = event.target.value.toUpperCase();
      },
    });

    this.#identity = el('div', {});
    this.#renderIdentity();

    this.#status = el('p', { className: 'status', text: 'Connessione al server...' });

    this.#root = el('div', { className: 'screen' }, [
      el('h1', { className: 'title', text: "Il gioco dello zio di Marz" }),
      this.#status,
      el('div', { className: 'panel' }, [
        this.#identity,
        this.#serverDetails,
        el('div', { className: 'row' }, [
          el('button', {
            className: 'button button-soft',
            type: 'button',
            text: 'Le tue statistiche',
            onclick: () => this.#handlers.onStats(this.serverUrl),
          }),
          // Opened on the game that is selected below, the way you would look up the rules of the
          // game you are about to start rather than of a list
          el('button', {
            className: 'button button-soft',
            type: 'button',
            text: 'Regole',
            onclick: () => this.#handlers.onRules(this.selectedGameName),
          }),
        ]),
      ]),
      el('div', { className: 'panel' }, [
        el('h2', { text: 'Crea una stanza' }),
        el('label', { for: 'gameType', text: 'Gioco' }),
        this.#gameSelect,
        this.#lockedHint,
        el('div', { className: 'row' }, [
          el('div', {}, [el('label', { for: 'playersNumber', text: 'Giocatori' }), this.#playersSelect]),
          el('div', {}, [el('label', { for: 'difficulty', text: 'Bot' }), this.#difficultySelect]),
        ]),
        el('button', {
          className: 'button button-gold',
          type: 'button',
          text: 'Crea',
          onclick: () => this.#submit('create'),
        }),
      ]),
      el('div', { className: 'panel' }, [
        el('h2', { text: 'Entra con un codice' }),
        el('label', { for: 'roomCode', text: 'Codice stanza' }),
        this.#codeInput,
        el('button', {
          className: 'button button-soft',
          type: 'button',
          text: 'Entra',
          onclick: () => this.#submit('join'),
        }),
      ]),
    ]);
  }

  // With an address it tries that one and reports how it went; without, it goes down the list of
  // candidates and keeps the first that answers. An address typed by hand is never silently
  // replaced by another: being sent to a server one did not ask for is worse than an error message
  // Signed in there is nothing to type: the name at the table is the name of the account, and
  // the server would override anything else anyway. Signed out the field is back, next to what
  // playing as a guest costs
  #renderIdentity() {
    if (!this.#identity) return;
    clear(this.#identity);
    if (this.#account) {
      this.#identity.append(
        el('p', { className: 'label', text: 'Giochi come' }),
        el('p', { className: 'account-name', text: this.#account.userName }),
        el('button', {
          className: 'button button-soft',
          type: 'button',
          text: 'Il tuo account',
          onclick: () => this.#handlers.onAccount(),
        }),
      );
      return;
    }
    this.#identity.append(
      el('label', { for: 'playerName', text: 'Nome' }),
      this.#nameInput,
      el('p', {
        className: 'stats-hint',
        text: 'Ospite: un solo gioco sbloccato, chiunque può presentarsi con questo nome e le partite restano solo su questo dispositivo. Nelle stanze degli amici entri comunque, col codice.',
      }),
      el('button', {
        className: 'button button-soft',
        type: 'button',
        text: 'Accedi o registrati',
        onclick: () => this.#handlers.onAccount(),
      }),
    );
  }

  async loadCatalogue(only = null) {
    const candidates = only ? [only] : candidateServerUrls();
    this.#setStatus('Connessione al server...', false);
    this.#catalogue = null;
    let lastError = null;
    for (const url of candidates) {
      try {
        this.#catalogue = await this.#handlers.onLoadCatalogue(url);
        this.#serverInput.value = url;
        remember(StorageKeys.serverUrl, url);
        this.#fillFromCatalogue();
        this.#setStatus(`Server raggiunto: ${serverLabel(url)}`, false);
        this.#serverDetails.open = false;
        return;
      } catch (error) {
        lastError = error;
      }
    }
    this.#setStatus(`Server non raggiungibile: ${lastError?.message ?? 'nessun indirizzo'}`, true);
    this.#serverDetails.open = true;
  }

  #fillFromCatalogue() {
    const chosen = this.#gameSelect.value;
    clear(this.#gameSelect);
    for (const game of this.#catalogue.games) {
      const gameType = game[Protocol.JSONGameTypeID];
      const unsupported = UNSUPPORTED_GAMES.has(gameType);
      const name = game[ServerProtocol.DisplayNameID];
      this.#gameSelect.append(
        el('option', {
          value: gameType,
          // Only what this client cannot draw at all is unselectable. A locked game stays
          // choosable and answers with the reason, which is more use than a dead entry
          disabled: unsupported,
          text: unsupported
            ? `${name} — solo su Android`
            : this.#locked(gameType)
              ? `${name} 🔒`
              : name,
        }),
      );
    }
    // A disabled first entry would leave the select showing something nobody can start, and a
    // padlocked one would leave it showing something this visitor cannot start either
    const playable = this.#catalogue.games.find(
      (game) =>
        !UNSUPPORTED_GAMES.has(game[Protocol.JSONGameTypeID]) &&
        !this.#locked(game[Protocol.JSONGameTypeID]),
    );
    const keep = chosen && !this.#locked(chosen) ? chosen : null;
    if (keep) this.#gameSelect.value = keep;
    else if (playable) this.#gameSelect.value = playable[Protocol.JSONGameTypeID];
    this.#lockedHint.hidden = Boolean(this.#account);
    clear(this.#difficultySelect);
    for (const difficulty of this.#catalogue.difficulties) {
      this.#difficultySelect.append(
        el('option', {
          value: difficulty[Protocol.JSONAIDifficultyID],
          text: difficulty[ServerProtocol.DisplayNameID],
        }),
      );
    }
    this.#refreshPlayerCounts();
  }

  #locked(gameType) {
    return !this.#account && gameType !== MainGame;
  }

  // A guest who picks a padlocked game is sent back to the main one and told why, on the screen
  // that can do something about it. The same tap is the question and the way to answer it
  #gameChosen() {
    if (this.#locked(this.#gameSelect.value)) {
      this.#gameSelect.value = MainGame;
      this.#refreshPlayerCounts();
      this.#setStatus('Questo gioco si sblocca con un account: è gratis e ci vuole un minuto', true);
      this.#handlers.onAccount();
      return;
    }
    this.#refreshPlayerCounts();
  }

  // Only the table sizes the chosen game can actually be dealt for, keeping the previous choice
  // when the new game still allows it
  #refreshPlayerCounts() {
    const game = this.#selectedGame();
    if (!game) return;
    const previous = this.#playersSelect.value;
    const counts = game[ServerProtocol.PlayerCountsID] ?? [];
    clear(this.#playersSelect);
    for (const count of counts) {
      this.#playersSelect.append(el('option', { value: String(count), text: String(count) }));
    }
    const keep = counts.map(String).includes(previous) ? previous : null;
    this.#playersSelect.value = keep ?? (counts.includes(4) ? '4' : String(counts[0] ?? 4));
  }

  #selectedGame() {
    return this.#catalogue?.games.find(
      (game) => game[Protocol.JSONGameTypeID] === this.#gameSelect.value,
    );
  }

  #setStatus(text, isError) {
    this.#status.textContent = text;
    this.#status.classList.toggle('status-error', Boolean(isError));
  }

  #submit(action) {
    // Signed in, the typed name is not even read: the server names the seat from the session
    const playerName = this.#account
      ? this.#account.userName
      : this.#nameInput.value.trim();
    const serverUrl = this.serverUrl;
    if (!playerName) return this.#setStatus('Inserisci il tuo nome', true);
    if (!serverUrl) return this.#setStatus("Inserisci l'indirizzo del server", true);

    if (!this.#account) remember(StorageKeys.playerName, playerName);
    remember(StorageKeys.serverUrl, serverUrl);
    const identity = {
      playerName,
      accountToken: accountToken(),
      sessionToken: sessionToken(),
    };

    if (action === 'join') {
      const roomId = this.#codeInput.value.trim();
      if (!roomId) return this.#setStatus('Inserisci il codice della stanza', true);
      return this.#handlers.onJoin({ serverUrl, ...identity, roomId });
    }

    const game = this.#selectedGame();
    if (!game) return this.#setStatus('Catalogo dei giochi non disponibile', true);
    if (UNSUPPORTED_GAMES.has(game[Protocol.JSONGameTypeID])) {
      return this.#setStatus("Questo gioco per ora si gioca solo sull'app Android", true);
    }
    // Only creating is locked: entering a room somebody else opened is how a guest is invited
    if (this.#locked(game[Protocol.JSONGameTypeID])) {
      this.#setStatus('Questo gioco si sblocca con un account: è gratis e ci vuole un minuto', true);
      return this.#handlers.onAccount();
    }
    return this.#handlers.onCreate({
      serverUrl,
      ...identity,
      gameType: game[Protocol.JSONGameTypeID],
      deckType: game[Protocol.JSONDeckTypeID],
      deckFormat: game[Protocol.JSONDeckFormatID],
      playersNumber: Number(this.#playersSelect.value),
      difficulty: this.#difficultySelect.value,
    });
  }
}

export class RoomView {
  #root;
  #code;
  #players;
  #hint;
  #startButton;
  #handlers;

  // handlers: { onStart, onLeave }
  constructor(handlers) {
    this.#handlers = handlers;
    this.#build();
  }

  get element() {
    return this.#root;
  }

  #build() {
    this.#code = el('p', { className: 'room-code' });
    this.#players = el('ul', { className: 'player-list' });
    this.#hint = el('p', { className: 'status' });
    this.#startButton = el('button', {
      className: 'button button-gold',
      type: 'button',
      text: 'Avvia partita',
      hidden: true,
      onclick: () => {
        this.#startButton.disabled = true;
        this.#handlers.onStart();
      },
    });

    this.#root = el('div', { className: 'screen' }, [
      el('div', { className: 'panel' }, [
        el('p', { className: 'label', text: 'Codice stanza' }),
        this.#code,
        el('button', {
          className: 'button button-soft',
          type: 'button',
          text: 'Copia il codice',
          onclick: () => this.#copyCode(),
        }),
      ]),
      el('div', { className: 'panel' }, [this.#players, this.#hint]),
      this.#startButton,
      el('button', {
        className: 'button button-soft',
        type: 'button',
        text: 'Esci',
        onclick: () => this.#handlers.onLeave(),
      }),
    ]);
  }

  async #copyCode() {
    try {
      await navigator.clipboard.writeText(this.#code.textContent);
    } catch {
      // Clipboard access needs a secure context: the code is on screen anyway
    }
  }

  update({ roomId, players, registered = [], seats, isHost }) {
    this.#code.textContent = roomId ?? '';
    clear(this.#players);
    players.forEach((name, index) => {
      // A seat with no account behind it is a name anybody could have typed, the way it always
      // was. Saying so is the whole visible difference accounts make at a table
      const guest = registered.length > index && registered[index] === false;
      this.#players.append(
        el('li', { className: index === 0 ? 'player host' : 'player' }, [
          el('span', { text: index === 0 ? '★' : '•' }),
          el('span', { text: name }),
          guest ? el('span', { className: 'player-guest', text: 'ospite' }) : null,
        ]),
      );
    });
    const free = Math.max(0, seats - players.length);
    this.#hint.textContent = isHost
      ? `Sei l'host. ${free === 0 ? 'Il tavolo è pieno.' : `${free} posti liberi diventeranno bot.`}`
      : "In attesa che l'host avvii la partita...";
    this.#startButton.hidden = !isHost;
    this.#startButton.disabled = false;
  }
}
